// ModuleListPage — Session C list-page factory.
//
// Every CRUD module's list page is the same shape: permission-gated FwTable
// over `GET <api>?sortBy&sortDir&offset&limit&query={}` (the exact request
// AgTable sent), a quick text filter, an optional locality filter, New /
// bulk-delete actions, and row → form navigation. This factory turns each
// page into a ~20-line config.
//
// Columns come from assets/files/agModule.json — the same definitions
// AgTable rendered, so nothing silently changes shape.
import { Fragment, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import PageBreadcrumb from "elements/System/PageBreadcrumb";
import AgDelete from "elements/System/AgDelete";
import FieldSelect from "elements/System/FieldSelect";
import FwTable, { FwColumn, FwQuery, FwFetchResult } from "elements/Table/FwTable";
import agModuleList from "assets/files/agModule.json";
import { api, common } from "includes";

export type ModuleListConfig = {
  /** AgTable module key — reused for saved-view storage + filter session. */
  module: string;
  title: string;
  api: string;
  /** Route base: /<url>/form?... */
  url: string;
  /** Column set key in agModule.json. */
  columnsKey: string;
  /** Which query key the quick text filter writes. */
  quickKey: string;
  quickPlaceholder: string;
  localityFilter?: boolean;
  /** [read, create, update, delete] permission codes. */
  perms: [string, string, string, string];
  /** Extra toolbar content (e.g. the Units CSV pair). */
  extraToolbar?: (reload: () => void) => React.ReactNode;
  /** Toolbar controls that act on the current selection and filter — see
   *  FwTable.bulkActions. `done()` clears the selection and reloads. */
  bulkActions?: (
    ids: string[],
    done: () => void,
    filter: Record<string, any>
  ) => React.ReactNode;
};

export default function ModuleListPage(cfg: ModuleListConfig) {
  const navigate = useNavigate();
  const [permission] = useState(common.getUserPermission());
  const [pendingDelete, setPendingDelete] = useState<{
    ids: string[];
    done: () => void;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [readP, createP, updateP, deleteP] = cfg.perms;

  if (!permission?.includes(readP)) {
    common.redirect("app/home");
  }

  const columns = ((agModuleList as any)[cfg.columnsKey] || []) as FwColumn[];

  // Identical request shape to AgTable's getData().
  const fetcher = useCallback(
    (q: FwQuery, cb: (r: FwFetchResult) => void) => {
      let query = `?sortBy=${q.sortBy}&sortDir=${q.sortDir}`;
      query += `&offset=${q.page + 1}&limit=${q.limit}`;
      query += `&query=${JSON.stringify(q.filter)}`;
      api.call(
        { url: cfg.api, method: "GET", auth: "token", query },
        (res: any) => {
          if (res.status === 200) {
            cb({
              rows: res.data?.data || [],
              total: res.data?.pageInfo?.totalCount || 0,
            });
          } else {
            cb({ rows: [], total: 0 });
          }
        }
      );
    },
    [cfg.api]
  );

  // Identical to AgTable's handleMassDelete endpoint.
  const massDelete = (ids: string[], done: () => void) => {
    api.call(
      {
        url: `${cfg.api}/mass-delete`,
        method: "POST",
        auth: "token",
        body: { ids },
      },
      (res: any) => {
        if (res.status === 200) {
          common.notify("S", "Selected items deleted successfully.");
          done();
          setReloadKey((k) => k + 1);
        } else {
          common.notify("E", "Failed to delete items.");
        }
        setPendingDelete(null);
      }
    );
  };

  return (
    permission?.includes(readP) && (
      <Fragment>
        <PageBreadcrumb
          breadCrumbItems={[{ label: cfg.title, path: `/${cfg.url}/list`, active: true }]}
          title={cfg.title}
        />

        <FwTable
          module={cfg.module}
          columns={columns}
          fetcher={fetcher}
          reloadKey={reloadKey}
          defaultSort={{ sortBy: "createdAt", sortDir: "desc" }}
          quickFilterKey={cfg.quickKey}
          quickFilterPlaceholder={cfg.quickPlaceholder}
          extraFilters={
            cfg.localityFilter
              ? (filter, set) => (
                  <div className="fw-select" style={{ minWidth: 190 }}>
                    <FieldSelect
                      api="admin/locality"
                      query="?offset=1&limit=1000"
                      get="name"
                      set="_id"
                      value={null}
                      placeholder="All localities"
                      render={"yes"}
                      clearable={true}
                      multi={false}
                      onSelect={(data: any) =>
                        set({ locality: data?.value ? [data.value] : [] })
                      }
                    />
                  </div>
                )
              : undefined
          }
          toolbar={
            <Fragment>
              {cfg.extraToolbar?.(() => setReloadKey((k) => k + 1))}
              {permission.includes(createP) && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(`/${cfg.url}/form?mode=add`)}
                >
                  <i className="ri-add-line" /> New
                </button>
              )}
            </Fragment>
          }
          bulkActions={
            cfg.bulkActions
              ? (ids, done, filter) =>
                  cfg.bulkActions!(ids, () => {
                    done();
                    setReloadKey((k) => k + 1);
                  }, filter)
              : undefined
          }
          onDeleteSelected={
            permission.includes(deleteP)
              ? (ids, done) => setPendingDelete({ ids, done })
              : undefined
          }
          actions={(row) => (
            <Fragment>
              <button
                type="button"
                className="fw-rowact"
                title="View"
                onClick={() => navigate(`/${cfg.url}/form?id=${row._id}&mode=view`)}
              >
                <i className="ri-eye-line" />
              </button>
              {permission.includes(updateP) && (
                <button
                  type="button"
                  className="fw-rowact"
                  title="Edit"
                  onClick={() => navigate(`/${cfg.url}/form?id=${row._id}&mode=edit`)}
                >
                  <i className="ri-edit-line" />
                </button>
              )}
            </Fragment>
          )}
          onRowOpen={(row) => navigate(`/${cfg.url}/form?id=${row._id}&mode=view`)}
          emptyText={`No ${cfg.title.toLowerCase()} match this view.`}
        />

        {pendingDelete && (
          <AgDelete
            type="delete"
            onAgDeleteCallBack={(isDelete: boolean) => {
              if (isDelete) {
                massDelete(pendingDelete.ids, pendingDelete.done);
              } else {
                setPendingDelete(null);
              }
            }}
          />
        )}
      </Fragment>
    )
  );
}
